import type { Family, FamilyMembership, Location, Notification, ProfileManagementGrant, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { FamilyRepository } from '../types';
import { MockDb } from './MockDb';

export class MockFamilyRepository implements FamilyRepository {
  constructor(private db: MockDb) {}

  async getFamily(): Promise<Family> {
    await this.db.read();
    /*
     * A copy, never the row itself. The mock changes its rows in place, and a
     * caller holding the very object that was changed cannot tell that anything
     * happened: the screen that had just saved a new welcome message went on
     * showing the old one, because to React it was "the same family" it already
     * had. A real backend hands back a fresh object every time; so does this.
     */
    const fam = this.db.t.families[0];
    return fam && { ...fam };
  }

  async getMembership(accountId: UUID): Promise<FamilyMembership | undefined> {
    await this.db.read();
    return this.db.membership(accountId);
  }

  async listMemberships(): Promise<FamilyMembership[]> {
    await this.db.read();
    return [...this.db.t.family_memberships];
  }

  async listGrants(): Promise<ProfileManagementGrant[]> {
    await this.db.read();
    return [...this.db.t.profile_management_grants];
  }

  async listGrantsForHelper(accountId: UUID): Promise<ProfileManagementGrant[]> {
    await this.db.read();
    return this.db.t.profile_management_grants.filter((g) => g.helperAccountId === accountId && g.status === 'active');
  }

  async listLocations(): Promise<Location[]> {
    await this.db.read();
    return [...this.db.t.locations];
  }

  async addLocation(label: string, labelHe?: string): Promise<Location> {
    await this.db.write();
    const existing = this.db.t.locations.find((l) => l.label.toLowerCase() === label.trim().toLowerCase());
    if (existing) return existing;
    const loc: Location = { id: uuid(), label: label.trim(), labelHe: labelHe?.trim() || undefined };
    this.db.t.locations.push(loc);
    await this.db.commit(['locations']);
    return loc;
  }

  async listNotifications(accountId: UUID): Promise<Notification[]> {
    await this.db.read();
    return this.db.t.notifications.filter((n) => n.accountId === accountId).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async markNotificationRead(id: UUID): Promise<void> {
    await this.db.ready;
    const n = this.db.t.notifications.find((x) => x.id === id);
    if (n && !n.read) {
      n.read = true;
      await this.db.commit(['notifications']);
    }
  }

  async markWelcomeSeen(): Promise<void> {
    await this.db.ready;
    const session = this.db.getSession();
    const mine = session ? this.db.membership(session.accountId) : undefined;
    if (!mine) return;
    mine.welcomeSeenAt = nowISO();
    await this.db.commit(['family_memberships']);
  }
}
